import { audioRecordingModule } from "@/modules/audio-recording";
import { uuidModule } from "@/modules/uuid";
import { useAppStore } from "@/store";
import { useNoteSourceArtifactInfo } from "@/components/note/editor/source-bar/utils/use-note-source-artifact-info";
import { NoteSourceAudioArtifactInfo } from "@/components/note/editor/source-bar/types";
import { AudioSourceAvailabilityState } from "./audio-source-availability";

const LOCAL_AUDIO_MIME_TYPE = "application/x-recall-pcm;sr=16000;ch=1;bits=16";

const getDurationSeconds = ({
  durationMs,
}: {
  durationMs: number | null | undefined;
}): number | null => {
  if (typeof durationMs !== "number" || !Number.isFinite(durationMs)) {
    return null;
  }

  return Math.max(0, Math.round(durationMs / 1000));
};

export const useAudioRecordingNoteSourceArtifactInfo = ({
  sourceId,
  isOpen,
  audioRecordingId,
  availabilityState,
  isEnabled,
}: {
  sourceId: string;
  isOpen: boolean;
  audioRecordingId: string | null;
  availabilityState: AudioSourceAvailabilityState;
  isEnabled: boolean;
}): NoteSourceAudioArtifactInfo => {
  const shouldFetchRemote =
    isEnabled && isOpen && availabilityState === AudioSourceAvailabilityState.Ready;

  const remoteArtifactInfo = useNoteSourceArtifactInfo({
    sourceId,
    isOpen: shouldFetchRemote,
    sourceKind: "AUDIO",
  });

  const { store } = useAppStore();

  const remoteAudioArtifactInfo = remoteArtifactInfo?.kind === "AUDIO" ? remoteArtifactInfo : null;

  const shouldUseLocalFallback =
    isEnabled &&
    isOpen &&
    (availabilityState !== AudioSourceAvailabilityState.Ready || !remoteAudioArtifactInfo?.data);
  const recording =
    shouldUseLocalFallback && audioRecordingId ? store.audioRecordings.get(audioRecordingId) : null;
  const observedDurationMs = shouldUseLocalFallback ? recording?.durationMs : null;
  const localTranscriptTurns = shouldUseLocalFallback ? (recording?.transcriptTurns ?? []) : [];
  const { transcriptArray, transcriptText } =
    audioRecordingModule.buildTranscriptPayload(localTranscriptTurns);
  const hasLocalTranscript = transcriptArray.length > 0 || Boolean(transcriptText.trim());

  const localArtifactData =
    shouldUseLocalFallback && audioRecordingId && hasLocalTranscript
      ? {
          artifact_kind: "AUDIO" as const,
          captured_audio_id: uuidModule.resolveAudioRecordingCapturedAudioUuid({
            associatedAudioRecordingId: audioRecordingId,
          }),
          mime_type: LOCAL_AUDIO_MIME_TYPE,
          transcript_text: transcriptText.trim() ? transcriptText : null,
          transcript_array: transcriptArray.length ? transcriptArray : null,
          audio_duration_seconds: getDurationSeconds({ durationMs: observedDurationMs }),
          download_url: "",
        }
      : null;

  const data =
    shouldUseLocalFallback && localArtifactData
      ? localArtifactData
      : (remoteAudioArtifactInfo?.data ?? null);

  return {
    kind: "AUDIO",
    data,
    isLoading: remoteArtifactInfo?.isLoading ?? false,
  };
};
